// AdminBrukere — søkbar bruker-liste med badges, VIP-diagnose, sortering og filtre.
// Owner+admin. Kolonner: brukernavn, e-post, reg-dato, sist pålogget,
// VIP/BankID/Creator/Selger badges, VIP-status (diagnose), kode brukt.
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, X, Check, Minus, RefreshCw } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import Layout from "../../components/Layout";
import { toast } from "sonner";

const PER_PAGE = 25;

const FILTERS = [
  { key: "is_vip", label: "VIP" },
  { key: "bankid_verified", label: "BankID" },
  { key: "is_creator", label: "Creator" },
  { key: "seller_verified", label: "Selger" },
  { key: "has_promo", label: "Kode" },
];

const SORTS = [
  { value: "registered_desc", label: "Nyeste først (reg-dato)" },
  { value: "registered_asc",  label: "Eldste først (reg-dato)" },
  { value: "last_login_desc", label: "Sist pålogget (nyeste)" },
  { value: "last_login_asc",  label: "Sist pålogget (eldste)" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("nb-NO", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

function Badge({ on, label }) {
  return (
    <span
      className={`inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-meta tracking-wider border ${
        on ? "border-[var(--magenta)]/60 text-[var(--magenta)] bg-[var(--magenta)]/10"
           : "border-white/10 text-[var(--cream-faint)]"
      }`}
      data-testid={`admin-badge-${label.toLowerCase()}-${on ? "yes" : "no"}`}
    >
      {label} {on ? "✓" : "—"}
    </span>
  );
}

function TriState({ value, onChange, label }) {
  // value: null (alle), true (ja), false (nei)
  const next = () => {
    if (value === null) onChange(true);
    else if (value === true) onChange(false);
    else onChange(null);
  };
  const icon = value === true ? <Check className="w-3 h-3" />
             : value === false ? <X className="w-3 h-3" />
             : <Minus className="w-3 h-3" />;
  const cls = value === true ? "border-emerald-500/60 text-emerald-400 bg-emerald-500/10"
            : value === false ? "border-red-500/60 text-red-400 bg-red-500/10"
            : "border-white/15 text-[var(--cream-faint)]";
  return (
    <button
      onClick={next}
      className={`flex items-center gap-1 px-2 py-1 border text-xs font-meta tracking-wider transition ${cls}`}
      data-testid={`admin-filter-${label.toLowerCase()}`}
    >
      {icon}
      <span>{label}</span>
      <span className="text-[10px] opacity-70">
        {value === null ? "alle" : value === true ? "ja" : "nei"}
      </span>
    </button>
  );
}

export default function AdminBrukere() {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("registered_desc");
  const [filters, setFilters] = useState({
    is_vip: null, bankid_verified: null, is_creator: null, seller_verified: null, has_promo: null,
  });
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.post("/admin/users/search-full", {
        q, sort, filters, page, per_page: PER_PAGE,
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setHasNext(!!res.data.has_next);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [q, sort, filters, page]);

  // Debounce søk: bare nullstill side ved q-endring. `load` kalles av effekten under.
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  // Kall load ved alle relevante endringer (inkl. page via NESTE/FORRIGE).
  useEffect(() => { load(); }, [sort, filters, page, load]);

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-4 sm:p-6" data-testid="admin-brukere-page">
        <div className="mb-6">
          <h1 className="font-editorial text-3xl text-[var(--cream)] mb-1">Brukere</h1>
          <p className="text-sm text-[var(--cream-soft)]">
            Alle registrerte brukere. Klikk badge-filter for å veksle alle/ja/nei. Klikk brukernavn for profil.
          </p>
        </div>

        {/* Søk + sort */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-[var(--cream-faint)] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Søk brukernavn, e-post eller ID"
              className="input-editorial w-full !pl-10"
              data-testid="admin-brukere-search"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
            className="input-editorial sm:w-64"
            data-testid="admin-brukere-sort"
          >
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button
            onClick={load}
            className="px-3 py-2 border border-white/15 text-xs font-meta tracking-wider hover:border-[var(--magenta)] hover:text-[var(--magenta)] flex items-center gap-2"
            data-testid="admin-brukere-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> OPPDATER
          </button>
        </div>

        {/* Filtre */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-brukere-filters">
          {FILTERS.map((f) => (
            <TriState
              key={f.key}
              value={filters[f.key]}
              onChange={(v) => setFilter(f.key, v)}
              label={f.label}
            />
          ))}
        </div>

        <div className="text-xs text-[var(--cream-faint)] mb-2" data-testid="admin-brukere-total">
          {loading ? "Laster…" : `${total} treff · side ${page}`}
        </div>

        {/* Tabell */}
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-sm" data-testid="admin-brukere-table">
            <thead className="bg-white/5 text-[var(--cream-faint)] text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-3 py-2">Brukernavn</th>
                <th className="text-left px-3 py-2">E-post</th>
                <th className="text-left px-3 py-2">Reg-dato</th>
                <th className="text-left px-3 py-2">Sist pålogget</th>
                <th className="text-left px-3 py-2">VIP</th>
                <th className="text-left px-3 py-2">BankID</th>
                <th className="text-left px-3 py-2">Creator</th>
                <th className="text-left px-3 py-2">Selger</th>
                <th className="text-left px-3 py-2">VIP-status</th>
                <th className="text-left px-3 py-2">Kode brukt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map((u) => {
                const evig = u.vip_status_debug === "EVIG (bug?)";
                const utlopt = (u.vip_status_debug || "").startsWith("UTLØPT");
                return (
                  <tr key={u.id} className="hover:bg-white/5" data-testid={`admin-brukere-row-${u.username}`}>
                    <td className="px-3 py-2">
                      <Link
                        to={`/profile/${u.username}`}
                        className="text-[var(--magenta)] hover:underline font-meta"
                        data-testid={`admin-brukere-link-${u.username}`}
                      >
                        @{u.username}
                      </Link>
                      {u.role && u.role !== "user" && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 border border-amber-500/40 text-amber-400">
                          {u.role.toUpperCase()}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[var(--cream-soft)]">
                      {u.email || "—"}
                      {!u.email_verified && u.email && (
                        <div className="text-[10px] text-amber-400">uverifisert</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[var(--cream-soft)] whitespace-nowrap">{fmtDate(u.created_at)}</td>
                    <td className="px-3 py-2 text-[var(--cream-soft)] whitespace-nowrap">{fmtDate(u.last_seen)}</td>
                    <td className="px-3 py-2"><Badge on={u.is_vip} label="VIP" /></td>
                    <td className="px-3 py-2"><Badge on={u.bankid_verified} label="BankID" /></td>
                    <td className="px-3 py-2"><Badge on={u.is_creator} label="Creator" /></td>
                    <td className="px-3 py-2"><Badge on={u.seller_verified} label="Selger" /></td>
                    <td className="px-3 py-2">
                      <span
                        className={`text-[11px] ${
                          evig ? "text-red-400 font-bold" :
                          utlopt ? "text-amber-400" :
                          "text-[var(--cream-soft)]"
                        }`}
                        data-testid={`admin-brukere-vipdebug-${u.username}`}
                      >
                        {u.vip_status_debug}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[11px] text-[var(--cream-soft)]">
                      {u.promo_code_used ? (
                        <>
                          <div className="font-meta text-[var(--magenta)]">{u.promo_code_used.code}</div>
                          <div className="opacity-70">
                            {u.promo_code_used.kind}
                            {u.promo_code_used.days ? ` · ${u.promo_code_used.days}d` : ""}
                          </div>
                        </>
                      ) : "—"}
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && !loading && (
                <tr>
                  <td colSpan="10" className="px-3 py-8 text-center text-[var(--cream-faint)]">
                    Ingen brukere matcher.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginering */}
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
            className="px-3 py-2 border border-white/15 text-xs font-meta tracking-wider disabled:opacity-40 hover:border-[var(--magenta)]"
            data-testid="admin-brukere-prev"
          >
            ← FORRIGE
          </button>
          <span className="text-xs text-[var(--cream-faint)]">Side {page}</span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasNext || loading}
            className="px-3 py-2 border border-white/15 text-xs font-meta tracking-wider disabled:opacity-40 hover:border-[var(--magenta)]"
            data-testid="admin-brukere-next"
          >
            NESTE →
          </button>
        </div>
      </div>
    </Layout>
  );
}
